import type { CopilotMessage, CopilotReasoning } from "./types";
import { mergeRichContent } from "./richContent";

const mergeReasoning = (
  left?: CopilotReasoning,
  right?: CopilotReasoning
): CopilotReasoning | undefined => {
  if (!left) return right;
  if (!right) return left;
  return {
    content: [left.content.trim(), right.content.trim()]
      .filter(Boolean)
      .join("\n"),
    durationMs: (left.durationMs ?? 0) + (right.durationMs ?? 0) || undefined,
    active: left.active || right.active,
  };
};

const mergeAssistantMessages = (
  left: CopilotMessage,
  right: CopilotMessage
): CopilotMessage => {
  const tools = new Map(
    [...(left.tools ?? []), ...(right.tools ?? [])].map((tool) => [
      tool.id,
      tool,
    ])
  );
  // Error fields describe one failure, so take them together from whichever
  // round carries the error (normally the final one).
  const errorSource = right.error ? right : left;
  return {
    ...left,
    content: [left.content.trim(), right.content.trim()]
      .filter(Boolean)
      .join("\n\n"),
    richContent: mergeRichContent(left.richContent, right.richContent),
    reasoning: mergeReasoning(left.reasoning, right.reasoning),
    tools: [...tools.values()],
    error: errorSource.error,
    errorTitle: errorSource.errorTitle,
    errorKind: errorSource.errorKind,
    retryable: errorSource.retryable,
    truncated: right.truncated ?? left.truncated,
    stopped: right.stopped ?? left.stopped,
  };
};

/** Consecutive assistant rounds that only contain reasoning/tools are one work
 * phase. Keep merging them until Neo emits visible answer text or an error. */
export const groupCopilotMessages = (
  messages: CopilotMessage[]
): CopilotMessage[] => {
  const grouped: CopilotMessage[] = [];
  let pendingAssistant: CopilotMessage | null = null;

  messages.forEach((message) => {
    if (message.role === "user") {
      if (pendingAssistant) grouped.push(pendingAssistant);
      pendingAssistant = null;
      grouped.push(message);
      return;
    }

    const combined = pendingAssistant
      ? mergeAssistantMessages(pendingAssistant, message)
      : message;
    if (
      combined.content.trim() ||
      (combined.richContent?.length ?? 0) > 0 ||
      combined.error
    ) {
      grouped.push(combined);
      pendingAssistant = null;
    } else {
      pendingAssistant = combined;
    }
  });

  if (pendingAssistant) grouped.push(pendingAssistant);
  return grouped;
};
